import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent } from '../../../../components/ui/Card.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/FormField.js';
import { StatusBadge } from '../../../../components/ui/StatusBadge.js';
import { Money } from '../../../../components/ui/Money';
import { useToast } from '../../../../components/ui/Toast.js';
import { Search, Plus, Scale, AlertTriangle } from 'lucide-react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../lib/locale';

import { useDecrees, useUpdateDecreeStatus } from '../../../../features/finance/hooks';
import { TableSkeleton } from '../../../../components/ui/Skeleton';
import { EmptyState } from '../../../../components/ui/EmptyState';
import { usePageTitle } from '../../../../lib/hooks';
import { PageHeader } from '../../../../components/layout/PageHeader';

export const Route = createFileRoute('/_auth/finance/decrees/')({
  component: LegalDecreesPage,
});

interface DecreeRecord {
  id: string;
  decreeNumber: string;
  subscriberId: string | null;
  subscriberName: string | null;
  ticketNumber: number | null;
  amountPaise: number;
  courtFeesPaise: number;
  decreeDate: string;
  status: 'FILING' | 'EXECUTION' | 'SETTLED' | 'APPEALED';
}

const STATUS_BADGE: Record<DecreeRecord['status'], 'success' | 'info' | 'danger' | 'warning'> = {
  SETTLED: 'success',
  APPEALED: 'info',
  EXECUTION: 'danger',
  FILING: 'warning',
};

function LegalDecreesPage() {
  usePageTitle('Legal Decrees');
  const { toast } = useToast();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const { data: response, isLoading, isError, error, refetch } = useDecrees(query ? { query } : undefined);
  const decrees = (response?.data ?? []) as DecreeRecord[];

  const updateStatusMutation = useUpdateDecreeStatus();

  const handleUpdateStatus = (id: string, newStatus: DecreeRecord['status']) => {
    updateStatusMutation.mutate({ id, status: newStatus }, {
      onSuccess: () => {
        toast({
          type: 'success',
          title: 'Decree Status Updated',
          description: `Decree record status has been updated to ${newStatus}.`,
        });
      },
      onError: (err: unknown) => {
        toast({
          type: 'error',
          title: 'Failed to Update Status',
          description: (err as { message?: string })?.message || 'Could not update the decree status. Please try again.',
        });
      },
    });
  };

  const filteredDecrees = decrees.filter(d =>
    d.decreeNumber.toLowerCase().includes(query.toLowerCase()) ||
    (d.subscriberName ?? '').toLowerCase().includes(query.toLowerCase()) ||
    String(d.ticketNumber ?? '').toLowerCase().includes(query.toLowerCase())
  );

  const totalPaiseInLitigation = decrees.filter(d => d.status !== 'SETTLED').reduce((acc, d) => acc + d.amountPaise, 0);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Legal Decrees & Awards"
        subtitle="Track court awards, arbitration decrees, execution filings, and court fees incurred on delinquent subscribers."
        action={
          <Button variant="primary" onClick={() => navigate({ to: '/finance/decrees/new' })}>
            <Plus className="w-4 h-4 mr-2" />
            Log Decree Case
          </Button>
        }
      />
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
          <Input
            type="text"
            placeholder="Search case #, subscriber, ticket..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full h-9 pl-9 pr-3"
          />
        </div>
      </div>
      <div className="flex-1 w-full min-h-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mb-6">
          <Card className="shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Total Awards Under Litigation</span>
                <h2 className="text-2xl font-bold text-danger-600">
                  <Money paise={totalPaiseInLitigation} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                </h2>
              </div>
              <div className="p-3 bg-danger-bg text-danger-text rounded-lg">
                <Scale className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>

          <div className="bg-surface-2/40 border border-border rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-warning-500 shrink-0 mt-0.5" />
            <div className="text-xs text-text-secondary leading-relaxed">
              <span className="font-bold text-text-primary">Filing Policy:</span> Under standard Indian recovery rules, legal fees and court arbitration charges logged under a decree are added to the defaulting member's ledger as receivable fines.
            </div>
          </div>
        </div>

        <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
          {isLoading ? (
            <TableSkeleton columns={8} rows={5} />
          ) : isError ? (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load decrees</p>
              <p className="text-xs text-text-muted mt-1">{error?.message || 'Unknown error'}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          ) : filteredDecrees.length === 0 ? (
            <EmptyState
              title="No decrees found"
              description={query ? 'No records match your search.' : 'No legal decree cases have been logged yet.'}
              icon={<Scale className="w-8 h-8" />}
            />
          ) : (
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                <tr>
                  <th className="p-4">Case Number</th>
                  <th className="p-4">Subscriber Name</th>
                  <th className="p-4">Ticket</th>
                  <th className="p-4">Awarded principal</th>
                  <th className="p-4">Court Fees Logged</th>
                  <th className="p-4">Hearing Date</th>
                  <th className="p-4">Decree status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary">
                {filteredDecrees.map(d => (
                  <tr key={d.id} className="hover:bg-surface-2 transition-colors">
                    <td className="p-4 font-mono text-xs font-bold">{d.decreeNumber}</td>
                    <td className="p-4 font-bold">{d.subscriberName ?? '—'}</td>
                    <td className="p-4 font-mono text-xs">{d.ticketNumber ?? '—'}</td>
                    <td className="p-4">
                      <Money paise={d.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                    </td>
                    <td className="p-4 text-danger-600 font-semibold">
                      <Money paise={d.courtFeesPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                    </td>
                    <td className="p-4 font-mono text-xs">
                      {new Date(d.decreeDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="p-4">
                      <StatusBadge status={STATUS_BADGE[d.status]} label={d.status} />
                    </td>
                    <td className="p-4 text-right">
                      {d.status === 'FILING' && (
                        <Button variant="ghost" size="sm" onClick={() => handleUpdateStatus(d.id, 'EXECUTION')}>
                          File Execution
                        </Button>
                      )}
                      {(d.status === 'EXECUTION' || d.status === 'APPEALED') && (
                        <Button variant="ghost" size="sm" className="text-success-500 hover:text-success-text" onClick={() => handleUpdateStatus(d.id, 'SETTLED')}>
                          Mark Settled
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
